/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ============================================================================
// SHADER SOURCE CODE
// ============================================================================

const VERTEX_SHADER = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = a_texCoord;
}
`;

const BLUR_HORIZONTAL_FRAG = `
precision mediump float;

uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform float u_blurRadius;
varying vec2 v_texCoord;

const int MAX_KERNEL_SIZE = 15;

void main() {
  vec4 color = vec4(0.0);
  float totalWeight = 0.0;

  float pixelSize = 1.0 / u_resolution.x;
  float kernelSize = min(u_blurRadius, float(MAX_KERNEL_SIZE));
  float sigma = kernelSize * 0.5;
  float invSigma2 = -0.5 / (sigma * sigma + 0.001);

  for (int i = -MAX_KERNEL_SIZE; i <= MAX_KERNEL_SIZE; i++) {
    float fi = float(i);
    if (abs(fi) > kernelSize) continue;

    float weight = exp(fi * fi * invSigma2);
    vec2 offset = vec2(fi * pixelSize * u_blurRadius / kernelSize, 0.0);
    color += texture2D(u_texture, v_texCoord + offset) * weight;
    totalWeight += weight;
  }

  gl_FragColor = color / totalWeight;
}
`;

const BLUR_VERTICAL_FRAG = `
precision mediump float;

uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform float u_blurRadius;
varying vec2 v_texCoord;

const int MAX_KERNEL_SIZE = 15;

void main() {
  vec4 color = vec4(0.0);
  float totalWeight = 0.0;

  float pixelSize = 1.0 / u_resolution.y;
  float kernelSize = min(u_blurRadius, float(MAX_KERNEL_SIZE));
  float sigma = kernelSize * 0.5;
  float invSigma2 = -0.5 / (sigma * sigma + 0.001);

  for (int i = -MAX_KERNEL_SIZE; i <= MAX_KERNEL_SIZE; i++) {
    float fi = float(i);
    if (abs(fi) > kernelSize) continue;

    float weight = exp(fi * fi * invSigma2);
    vec2 offset = vec2(0.0, fi * pixelSize * u_blurRadius / kernelSize);
    color += texture2D(u_texture, v_texCoord + offset) * weight;
    totalWeight += weight;
  }

  gl_FragColor = color / totalWeight;
}
`;

// MW-12383: highp is required for the snoise/permute math used by ridged().
// permute computes ((x * 34.0) + 1.0) * x where x can reach ~125 on Android Chrome,
// producing intermediate values >500k that overflow mediump (max ~16k).
// Desktop GPUs silently promote; PowerVR/Mali honor mediump and emit NaN, contaminating alpha.
const DOWNSAMPLE_FRAG = `
precision highp float;

uniform sampler2D u_texture;
uniform float u_scale;
uniform float u_time;
uniform float u_distortion;
uniform float u_flipY;
varying vec2 v_texCoord;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
                           dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float ridged(vec2 p, float t) {
  float value = 0.0;
  float amplitude = 1.0;
  float frequency = 1.0;
  float prev = 1.0;
  float offset = vec2(t * 0.12, t * -0.08).x;
  for (int i = 0; i < 3; i++) {
    float n = snoise(p * frequency + offset);
    n = 1.0 - abs(n);
    n = n * n;
    n *= prev;
    prev = n;
    value += n * amplitude;
    frequency *= 2.1;
    amplitude *= 0.5;
    offset *= -1.3;
  }
  return value * 0.5;
}

void main() {
  vec2 srcUV = vec2(v_texCoord.x, u_flipY > 0.5 ? 1.0 - v_texCoord.y : v_texCoord.y);
  vec2 zoomedCoord = (srcUV - 0.5) * u_scale + 0.5;
  zoomedCoord = clamp(zoomedCoord, 0.0, 1.0);
  vec4 color = texture2D(u_texture, zoomedCoord);

  if (u_distortion > 0.0) {
    float edgeDist = min(min(v_texCoord.x, 1.0 - v_texCoord.x),
                         min(v_texCoord.y, 1.0 - v_texCoord.y));

    float ridge = ridged(v_texCoord * 2.0, u_time);

    float edgeFade = smoothstep(0.0, 0.35, edgeDist);
    float warpStrength = (1.0 - edgeFade) * u_distortion;
    float distortedEdge = edgeDist + (ridge - 0.3) * warpStrength;
    color *= smoothstep(0.0, 0.04, distortedEdge);
  }

  gl_FragColor = color;
}
`;

const COMPOSITE_FRAG = `
precision mediump float;

uniform sampler2D u_texture;
uniform float u_saturation;
uniform vec2 u_shaveFrac;
varying vec2 v_texCoord;

vec3 adjustSaturation(vec3 color, float amount) {
  float luminance = dot(color, vec3(0.299, 0.587, 0.114));
  return mix(vec3(luminance), color, amount);
}

void main() {
  vec4 color = texture2D(u_texture, v_texCoord);
  color.rgb = adjustSaturation(color.rgb, u_saturation);

  float fadeX = smoothstep(0.0, u_shaveFrac.x, v_texCoord.x) *
                smoothstep(0.0, u_shaveFrac.x, 1.0 - v_texCoord.x);
  float fadeY = smoothstep(0.0, u_shaveFrac.y, v_texCoord.y) *
                smoothstep(0.0, u_shaveFrac.y, 1.0 - v_texCoord.y);
  float mask = fadeX * fadeY;

  gl_FragColor = vec4(color.rgb * mask, mask);
}
`;

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface BackgroundBlurConfig {
	blurPixels?: number;
	downsampleFactor?: number;
	scale?: number;
	saturation?: number;
	distortionStrength?: number;
}

interface ProcessedBlurConfig {
	blurPixels: number;
	downsampleFactor: number;
	scale: number;
	saturation: number;
	distortionStrength: number;
	blurRadius: number;
	blurPasses: number;
	shaveFrac: [number, number];
}

const MAX_BLUR_PASSES = 3;

function calculateBlurParams(targetBlur: number): {
	blurRadius: number;
	blurPasses: number;
} {
	if (targetBlur === 0) {
		return { blurRadius: 0, blurPasses: 0 };
	}

	return {
		blurRadius: Math.min(targetBlur, 50),
		blurPasses: Math.min(MAX_BLUR_PASSES, Math.max(2, Math.ceil(targetBlur / 20))),
	};
}

// ============================================================================
// UNIFORM LOCATION CACHE
// ============================================================================

type UniformCache = Map<string, WebGLUniformLocation | null>;

function getOrCacheUniform(
	gl: WebGLRenderingContext,
	cache: Map<WebGLProgram, UniformCache>,
	program: WebGLProgram,
	name: string,
): WebGLUniformLocation | null {
	let programCache = cache.get(program);
	if (!programCache) {
		programCache = new Map();
		cache.set(program, programCache);
	}

	if (programCache.has(name)) {
		return programCache.get(name)!;
	}

	const loc = gl.getUniformLocation(program, name);
	programCache.set(name, loc);
	return loc;
}

// ============================================================================
// BLUR RENDERER CLASS
// ============================================================================

class BlurRenderer {
	private gl: WebGLRenderingContext;
	private sourceTexture: WebGLTexture;
	private texA: WebGLTexture;
	private texB: WebGLTexture;
	private fbA: WebGLFramebuffer;
	private fbB: WebGLFramebuffer;
	private downsampleProgram: WebGLProgram;
	private blurHProgram: WebGLProgram;
	private blurVProgram: WebGLProgram;
	private compositeProgram: WebGLProgram;
	private quadBuffer: WebGLBuffer;
	private uniformCache: Map<WebGLProgram, UniformCache> = new Map();
	private attrCache: Map<WebGLProgram, Map<string, number>> = new Map();
	public config: ProcessedBlurConfig;
	private width = 0;
	private height = 0;
	private canvas: HTMLCanvasElement;
	private corsErrorCount = 0;
	private static readonly MAX_CORS_RETRIES = 3;

	constructor(canvas: HTMLCanvasElement, config: ProcessedBlurConfig) {
		this.canvas = canvas;
		this.config = config;

		const gl = canvas.getContext("webgl", {
			alpha: true,
			antialias: false,
			depth: false,
			premultipliedAlpha: true,
			preserveDrawingBuffer: false,
			powerPreference: "low-power",
		});

		if (!gl) {
			throw new Error("WebGL not supported");
		}

		this.gl = gl;

		gl.clearColor(0, 0, 0, 0);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

		canvas.addEventListener("webglcontextlost", this.handleContextLost);
		canvas.addEventListener("webglcontextrestored", this.handleContextRestored);

		this.quadBuffer = this.createQuad();
		this.downsampleProgram = this.createProgram(VERTEX_SHADER, DOWNSAMPLE_FRAG);
		this.blurHProgram = this.createProgram(VERTEX_SHADER, BLUR_HORIZONTAL_FRAG);
		this.blurVProgram = this.createProgram(VERTEX_SHADER, BLUR_VERTICAL_FRAG);
		this.compositeProgram = this.createProgram(VERTEX_SHADER, COMPOSITE_FRAG);
		this.sourceTexture = this.createTexture();
		this.texA = this.createTexture();
		this.texB = this.createTexture();
		this.fbA = this.createFramebuffer(this.texA);
		this.fbB = this.createFramebuffer(this.texB);
	}

	private handleContextLost = (event: Event): void => {
		event.preventDefault();
	};

	private handleContextRestored = (): void => {
		try {
			const { gl } = this;

			this.uniformCache.clear();
			this.attrCache.clear();

			gl.clearColor(0, 0, 0, 0);
			gl.enable(gl.BLEND);
			gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

			this.quadBuffer = this.createQuad();
			this.downsampleProgram = this.createProgram(VERTEX_SHADER, DOWNSAMPLE_FRAG);
			this.blurHProgram = this.createProgram(VERTEX_SHADER, BLUR_HORIZONTAL_FRAG);
			this.blurVProgram = this.createProgram(VERTEX_SHADER, BLUR_VERTICAL_FRAG);
			this.compositeProgram = this.createProgram(VERTEX_SHADER, COMPOSITE_FRAG);
			this.sourceTexture = this.createTexture();
			this.texA = this.createTexture();
			this.texB = this.createTexture();
			this.fbA = this.createFramebuffer(this.texA);
			this.fbB = this.createFramebuffer(this.texB);

			if (this.width > 0 && this.height > 0) {
				this.resize(this.width, this.height);
			}
		} catch {
			// Context restoration failed
		}
	};

	private createShader(type: number, source: string): WebGLShader {
		const { gl } = this;
		const shader = gl.createShader(type);
		if (!shader) {
			throw new Error("Failed to create shader");
		}

		gl.shaderSource(shader, source);
		gl.compileShader(shader);

		if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
			const info = gl.getShaderInfoLog(shader);
			gl.deleteShader(shader);
			throw new Error(`Shader compilation failed: ${info}`);
		}

		return shader;
	}

	private createProgram(vertexSource: string, fragmentSource: string): WebGLProgram {
		const { gl } = this;
		const vertexShader = this.createShader(gl.VERTEX_SHADER, vertexSource);
		const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, fragmentSource);

		const program = gl.createProgram();
		if (!program) {
			throw new Error("Failed to create program");
		}

		gl.attachShader(program, vertexShader);
		gl.attachShader(program, fragmentShader);
		gl.linkProgram(program);

		if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
			const info = gl.getProgramInfoLog(program);
			gl.deleteProgram(program);
			throw new Error(`Program link failed: ${info}`);
		}

		gl.deleteShader(vertexShader);
		gl.deleteShader(fragmentShader);

		return program;
	}

	private createTexture(): WebGLTexture {
		const { gl } = this;
		const texture = gl.createTexture();
		if (!texture) {
			throw new Error("Failed to create texture");
		}

		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

		return texture;
	}

	private createFramebuffer(texture: WebGLTexture): WebGLFramebuffer {
		const { gl } = this;
		const fb = gl.createFramebuffer();
		if (!fb) {
			throw new Error("Failed to create framebuffer");
		}

		gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

		return fb;
	}

	private createQuad(): WebGLBuffer {
		const { gl } = this;
		const buffer = gl.createBuffer();
		if (!buffer) {
			throw new Error("Failed to create buffer");
		}

		// interleaved: position (x,y), texCoord (u,v)
		// prettier-ignore
		const vertices = new Float32Array([-1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0, -1, 1, 0, 0, 1, -1, 1, 1, 1, 1, 1, 0]);

		gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

		return buffer;
	}

	private useProgram(program: WebGLProgram): void {
		this.gl.useProgram(program);
	}

	private getAttribLocation(program: WebGLProgram, name: string): number {
		let programCache = this.attrCache.get(program);
		if (!programCache) {
			programCache = new Map();
			this.attrCache.set(program, programCache);
		}

		const cached = programCache.get(name);
		if (cached !== undefined) return cached;

		const loc = this.gl.getAttribLocation(program, name);
		programCache.set(name, loc);
		return loc;
	}

	private drawQuad(program: WebGLProgram): void {
		const { gl } = this;

		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);

		const posLoc = this.getAttribLocation(program, "a_position");
		const texLoc = this.getAttribLocation(program, "a_texCoord");

		gl.enableVertexAttribArray(posLoc);
		gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0);

		if (texLoc >= 0) {
			gl.enableVertexAttribArray(texLoc);
			gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 16, 8);
		}

		gl.drawArrays(gl.TRIANGLES, 0, 6);
	}

	private setUniform(program: WebGLProgram, name: string, value: number | number[], isInt = false): void {
		const { gl } = this;
		const loc = getOrCacheUniform(gl, this.uniformCache, program, name);
		if (!loc) return;

		if (Array.isArray(value)) {
			gl.uniform2fv(loc, value);
		} else if (isInt) {
			gl.uniform1i(loc, value);
		} else {
			gl.uniform1f(loc, value);
		}
	}

	resize(width: number, height: number): void {
		const { gl } = this;
		this.width = width;
		this.height = height;

		gl.viewport(0, 0, width, height);

		for (const tex of [this.texA, this.texB]) {
			gl.bindTexture(gl.TEXTURE_2D, tex);
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
		}
	}

	get hasCorsFailure(): boolean {
		return this.corsErrorCount >= BlurRenderer.MAX_CORS_RETRIES;
	}

	render(source: HTMLImageElement | HTMLVideoElement, time = 0): boolean {
		const { gl, config } = this;

		if (this.hasCorsFailure) return false;

		gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
		try {
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
		} catch {
			this.corsErrorCount++;
			return false;
		}

		const glError = gl.getError();
		if (glError !== gl.NO_ERROR) {
			this.corsErrorCount++;
			return false;
		}

		// Pass 1: Downsample with procedural distortion mask
		gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbA);
		gl.viewport(0, 0, this.width, this.height);
		gl.clear(gl.COLOR_BUFFER_BIT);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
		this.useProgram(this.downsampleProgram);
		this.setUniform(this.downsampleProgram, "u_texture", 0, true);
		this.setUniform(this.downsampleProgram, "u_scale", config.scale);
		this.setUniform(this.downsampleProgram, "u_time", time);
		this.setUniform(this.downsampleProgram, "u_distortion", config.distortionStrength);
		// Chrome's accelerated video upload path can orient HTMLVideoElement
		// frames opposite to HTMLImageElement. Using UNPACK_FLIP_Y_WEBGL with
		// video is unreliable on Android, so flip in the shader instead.
		this.setUniform(this.downsampleProgram, "u_flipY", source instanceof HTMLVideoElement ? 1 : 0);
		this.drawQuad(this.downsampleProgram);

		// Pass 2+: Multi-pass Gaussian blur (horizontal + vertical per pass)
		const resolution = [this.width, this.height];
		for (let pass = 0; pass < config.blurPasses; pass++) {
			gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbB);
			gl.clear(gl.COLOR_BUFFER_BIT);
			gl.bindTexture(gl.TEXTURE_2D, this.texA);
			this.useProgram(this.blurHProgram);
			this.setUniform(this.blurHProgram, "u_texture", 0, true);
			this.setUniform(this.blurHProgram, "u_resolution", resolution);
			this.setUniform(this.blurHProgram, "u_blurRadius", config.blurRadius);
			this.drawQuad(this.blurHProgram);

			gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbA);
			gl.clear(gl.COLOR_BUFFER_BIT);
			gl.bindTexture(gl.TEXTURE_2D, this.texB);
			this.useProgram(this.blurVProgram);
			this.setUniform(this.blurVProgram, "u_texture", 0, true);
			this.setUniform(this.blurVProgram, "u_resolution", resolution);
			this.setUniform(this.blurVProgram, "u_blurRadius", config.blurRadius);
			this.drawQuad(this.blurVProgram);
		}

		// Final pass: Composite with saturation + alpha mask
		gl.bindFramebuffer(gl.FRAMEBUFFER, null);
		gl.viewport(0, 0, this.width, this.height);
		gl.clear(gl.COLOR_BUFFER_BIT);
		gl.bindTexture(gl.TEXTURE_2D, this.texA);
		this.useProgram(this.compositeProgram);
		this.setUniform(this.compositeProgram, "u_texture", 0, true);
		this.setUniform(this.compositeProgram, "u_saturation", config.saturation);
		this.setUniform(this.compositeProgram, "u_shaveFrac", config.shaveFrac);
		this.drawQuad(this.compositeProgram);
		return true;
	}

	destroy(): void {
		const { gl, canvas } = this;

		canvas.removeEventListener("webglcontextlost", this.handleContextLost);
		canvas.removeEventListener("webglcontextrestored", this.handleContextRestored);

		gl.deleteTexture(this.sourceTexture);
		gl.deleteTexture(this.texA);
		gl.deleteTexture(this.texB);
		gl.deleteFramebuffer(this.fbA);
		gl.deleteFramebuffer(this.fbB);
		gl.deleteProgram(this.downsampleProgram);
		gl.deleteProgram(this.blurHProgram);
		gl.deleteProgram(this.blurVProgram);
		gl.deleteProgram(this.compositeProgram);
		gl.deleteBuffer(this.quadBuffer);

		this.uniformCache.clear();
		this.attrCache.clear();

		const loseContext = gl.getExtension("WEBGL_lose_context");
		loseContext?.loseContext();
	}
}

// ============================================================================
// WEBGL SUPPORT CHECK
// ============================================================================

let webGLSupportCached: boolean | undefined;

function supportsWebGL(): boolean {
	if (webGLSupportCached !== undefined) {
		return webGLSupportCached;
	}

	try {
		const canvas = document.createElement("canvas");
		const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
		const supported = !!gl;

		if (gl) {
			const loseContext = (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context");
			loseContext?.loseContext();
		}

		webGLSupportCached = supported;
		return supported;
	} catch {
		webGLSupportCached = false;
		return false;
	}
}

// ============================================================================
// REACT HOOK
// ============================================================================

const TARGET_FPS = 30;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

/**
 * When true, the hook renders one deterministic frame (u_time=0) and skips
 * the requestAnimationFrame loop. Set by Playwright visual regression via
 * page.addInitScript so screenshots aren't flaky against animated WebGL output.
 */
function isVisualTestMode(): boolean {
	if (typeof window === "undefined") return false;
	return (window as unknown as { __COSMOS_VISUAL_TEST__?: boolean }).__COSMOS_VISUAL_TEST__ === true;
}

interface UseBackgroundBlurProps {
	canvasRef: React.RefObject<HTMLCanvasElement | null>;
	sourceRef: React.RefObject<HTMLImageElement | HTMLVideoElement | null>;
	config?: BackgroundBlurConfig;
	enabled?: boolean;
	/** Pass the actual element to use as a dependency trigger for effects */
	sourceElement?: HTMLImageElement | HTMLVideoElement;
}

interface UseBackgroundBlurReturn {
	hasError: boolean;
}

export function useBackgroundBlur({
	canvasRef,
	sourceRef,
	config = {},
	enabled = true,
	sourceElement,
}: UseBackgroundBlurProps): UseBackgroundBlurReturn {
	const [corsError, setCorsError] = useState(false);

	const checkCorsFailure = useCallback(
		(renderer: BlurRenderer) => {
			if (renderer.hasCorsFailure && !corsError) {
				setCorsError(true);
			}
		},
		[corsError],
	);
	const fullConfig = useMemo(
		() => ({
			blurPixels: config.blurPixels ?? 0,
			downsampleFactor: config.downsampleFactor ?? 8,
			scale: config.scale ?? 1,
			saturation: config.saturation ?? 1,
			distortionStrength: config.distortionStrength ?? 0,
		}),
		[config.blurPixels, config.downsampleFactor, config.scale, config.saturation, config.distortionStrength],
	);

	const rendererRef = useRef<BlurRenderer | null>(null);
	const fallbackElementRef = useRef<HTMLElement | null>(null);
	const animationFrameRef = useRef<number>(0);
	const hasWebGLSupportRef = useRef<boolean>(true);

	useEffect(() => {
		return () => {
			rendererRef.current?.destroy();
			fallbackElementRef.current?.remove();
		};
	}, []);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		if (!enabled) {
			if (rendererRef.current) {
				rendererRef.current.destroy();
				rendererRef.current = null;
			}
			return;
		}

		canvas.style.display = "";

		let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
		let hasInitialRender = false;

		const handleResize = (width: number, height: number): void => {
			const dpr = window.devicePixelRatio || 1;
			const downsample = fullConfig.downsampleFactor;

			const physicalWidth = Math.max(1, Math.floor((width * dpr) / downsample));
			const physicalHeight = Math.max(1, Math.floor((height * dpr) / downsample));

			canvas.width = physicalWidth;
			canvas.height = physicalHeight;

			const blurPixelsOnCanvas = fullConfig.blurPixels * (dpr / downsample);

			const { blurRadius, blurPasses } = calculateBlurParams(blurPixelsOnCanvas);

			const shaveFrac: [number, number] = [0.4, 0.4];

			const processedConfig: ProcessedBlurConfig = {
				...fullConfig,
				blurRadius,
				blurPasses,
				shaveFrac,
			};

			let renderer = rendererRef.current;
			if (!renderer && hasWebGLSupportRef.current) {
				if (!supportsWebGL()) {
					hasWebGLSupportRef.current = false;
					canvas.style.display = "none";
				} else {
					try {
						renderer = new BlurRenderer(canvas, processedConfig);
						rendererRef.current = renderer;
					} catch {
						hasWebGLSupportRef.current = false;
						canvas.style.display = "none";

						const fallback = document.createElement("div");
						fallback.className = canvas.className;
						fallback.style.cssText = `
							position: absolute;
							inset: 0;
							background-size: cover;
							background-position: center;
							pointer-events: none;
						`;

						canvas.parentElement?.appendChild(fallback);
						fallbackElementRef.current = fallback;
					}
				}
			}

			if (renderer) {
				renderer.config = processedConfig;
			}

			renderer?.resize(physicalWidth, physicalHeight);

			const source = sourceRef.current;
			if (source) {
				const isVideo = source instanceof HTMLVideoElement;
				const isReady = isVideo
					? source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
					: source.complete && source.naturalWidth > 0;

				if (isReady && renderer) {
					requestAnimationFrame(() => {
						renderer.render(source);
						checkCorsFailure(renderer);
					});
				}

				const fallback = fallbackElementRef.current;
				if (fallback) {
					const src = isVideo ? source.currentSrc || source.src : source.src;
					fallback.style.backgroundImage = `url(${src})`;
					fallback.style.filter = `blur(${blurRadius * downsample}px) saturate(${fullConfig.saturation})`;
				}
			}

			if (!hasWebGLSupportRef.current) {
				canvas.style.display = "none";
			}
		};

		const observeTarget = canvas.parentElement ?? canvas;

		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;

			const { width, height } = entry.contentRect;

			if (width === 0 || height === 0) return;

			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}

			if (!hasInitialRender) {
				hasInitialRender = true;
				handleResize(width, height);
			} else {
				resizeTimeout = setTimeout(() => {
					handleResize(width, height);
				}, 150);
			}
		});

		resizeObserver.observe(observeTarget);

		return () => {
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
			resizeObserver.disconnect();
		};
	}, [canvasRef, sourceRef, fullConfig, enabled, checkCorsFailure, sourceElement]);

	useEffect(() => {
		const source = sourceRef.current;
		if (!source) {
			return;
		}

		const isVideo = source instanceof HTMLVideoElement;
		const hasDistortion = fullConfig.distortionStrength > 0;
		const testMode = isVisualTestMode();
		const needsLoop = !testMode && (isVideo || hasDistortion);
		let startTime = 0;
		let lastFrameTime = 0;

		const renderFrame = (timestamp?: number): void => {
			const renderer = rendererRef.current;
			if (!renderer || renderer.hasCorsFailure) return;

			if (hasDistortion && timestamp !== undefined && !testMode) {
				if (startTime === 0) startTime = timestamp;
				const elapsed = (timestamp - startTime) / 1000;
				renderer.render(source, elapsed);
			} else {
				renderer.render(source, 0);
			}

			checkCorsFailure(renderer);
		};

		// Visual-test mode: render exactly one deterministic frame (u_time=0).
		// For videos, pause and seek to t=0 before rendering so the blur is
		// frame-0 regardless of autoplay timing.
		if (testMode) {
			if (isVideo) {
				const renderOnce = (): void => {
					try {
						source.pause();
						if (source.currentTime !== 0) source.currentTime = 0;
					} catch {
						// ignore — external videos may not allow seek
					}
					renderFrame();
				};

				if (source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
					renderOnce();
				} else {
					source.addEventListener("canplay", renderOnce, { once: true });
					return () => {
						source.removeEventListener("canplay", renderOnce);
					};
				}
				return undefined;
			}

			if (source.complete && source.naturalWidth > 0) {
				renderFrame();
			} else {
				const handleLoad = (): void => renderFrame();
				source.addEventListener("load", handleLoad, { once: true });
				return () => source.removeEventListener("load", handleLoad);
			}
			return undefined;
		}

		if (needsLoop) {
			const loop = (timestamp: number): void => {
				const renderer = rendererRef.current;
				if (renderer?.hasCorsFailure) {
					checkCorsFailure(renderer);
					return;
				}
				const delta = timestamp - lastFrameTime;
				if (delta >= FRAME_INTERVAL) {
					lastFrameTime = timestamp - (delta % FRAME_INTERVAL);
					if (!isVideo || source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
						renderFrame(timestamp);
					}
				}
				animationFrameRef.current = requestAnimationFrame(loop);
			};

			const startLoop = (): void => {
				animationFrameRef.current = requestAnimationFrame(loop);
			};

			if (isVideo) {
				if (source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
					startLoop();
				} else {
					source.addEventListener("canplay", startLoop, { once: true });
					// Re-check after attaching listener to close the race window
					// where canplay fires between the readyState check and addEventListener
					if (source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
						source.removeEventListener("canplay", startLoop);
						startLoop();
					}
				}
			} else if (source.complete && source.naturalWidth > 0) {
				startLoop();
			} else {
				source.addEventListener("load", startLoop, { once: true });
			}

			return () => {
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
				}
				if (isVideo) {
					source.removeEventListener("canplay", startLoop);
				} else {
					source.removeEventListener("load", startLoop);
				}
			};
		}

		// Fall-through path: non-video, non-distortion — render once when image loads.
		// isVideo is guaranteed false here (otherwise needsLoop would be true),
		// but TS can't infer that through the earlier testMode return, so cast.
		const img = source as HTMLImageElement;
		if (img.complete && img.naturalWidth > 0) {
			renderFrame();
		} else {
			const handleLoad = (): void => renderFrame();
			img.addEventListener("load", handleLoad);
			return () => img.removeEventListener("load", handleLoad);
		}

		return undefined;
	}, [sourceRef, fullConfig.distortionStrength, checkCorsFailure, sourceElement]);

	return { hasError: corsError };
}
